import { uiText, useUiLanguage, currentLocale } from '@/i18n/interface';
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { User, Phone, MapPin, Heart, History, Shield, FileCheck } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface StudentDetailsData {
  profile: any;
  emergencyContact: any;
  medicalInfo: any;
  martialArtsBackground: any;
  disclaimerSignature: any;
  beltLevel: any;
}

interface StudentDetailsViewProps {
  studentId: string;
}

const StudentDetailsView: React.FC<StudentDetailsViewProps> = ({ studentId }) => {
  useUiLanguage();
  const [studentDetails, setStudentDetails] = useState<StudentDetailsData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (studentId) {
      fetchStudentDetails();
    }
  }, [studentId]);

  const fetchStudentDetails = async () => {
    setIsLoading(true);
    try {
      // Fetch profile data
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', studentId)
        .single();

      if (profileError) {
        console.error('Error fetching profile:', profileError);
        return;
      }

      // Fetch belt level if assigned
      let beltLevel = null;
      if (profileData.belt_level_id) {
        const { data: beltData, error: beltError } = await supabase
          .from('belt_levels')
          .select('*')
          .eq('id', profileData.belt_level_id)
          .single();

        if (!beltError && beltData) {
          beltLevel = beltData;
        }
      }

      // Fetch emergency contact
      const { data: emergencyData } = await supabase
        .from('emergency_contacts')
        .select('*')
        .eq('user_id', studentId)
        .maybeSingle();

      // Fetch medical info
      const { data: medicalData } = await supabase
        .from('medical_info')
        .select('*')
        .eq('user_id', studentId)
        .maybeSingle();

      // Fetch martial arts background
      const { data: backgroundData } = await supabase
        .from('martial_arts_background')
        .select('*')
        .eq('user_id', studentId)
        .maybeSingle();

      // Fetch disclaimer signature
      const { data: disclaimerData } = await supabase
        .from('disclaimer_signatures')
        .select('*')
        .eq('user_id', studentId)
        .maybeSingle();

      setStudentDetails({
        profile: profileData,
        emergencyContact: emergencyData,
        medicalInfo: medicalData,
        martialArtsBackground: backgroundData,
        disclaimerSignature: disclaimerData,
        beltLevel
      });

    } catch (error) {
      console.error('Error fetching student details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getBeltColorClass = (color: string) => {
    const colorMap: { [key: string]: string } = {
      white: 'bg-gray-100 text-gray-900 border border-gray-300',
      orange: 'bg-orange-500 text-white',
      blue: 'bg-blue-500 text-white',
      yellow: 'bg-yellow-400 text-black',
      green: 'bg-green-500 text-white',
      purple: 'bg-purple-500 text-white',
      brown: 'bg-amber-700 text-white',
      black: 'bg-black text-white'
    };
    return colorMap[color] || 'bg-gray-500 text-white';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return uiText("Not provided");
    return new Date(dateString).toLocaleDateString(currentLocale(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return <div className="text-center p-4">{uiText("Loading student details...")}</div>;
  }

  if (!studentDetails) {
    return <div className="text-center p-4">{uiText("No student details available.")}</div>;
  }

  const { profile, emergencyContact, medicalInfo, martialArtsBackground, disclaimerSignature, beltLevel } = studentDetails;

  return (
    <div className="space-y-6">
      {/* Personal Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {uiText("Personal Information")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="text-sm font-medium text-gray-500">{uiText("Full Name")}</span>
              <p className="text-sm">{profile.first_name} {profile.last_name}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">{uiText("Email")}</span>
              <p className="text-sm">{profile.email}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">{uiText("Date of Birth")}</span>
              <p className="text-sm">{formatDate(profile.date_of_birth)}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">{uiText("Age Group")}</span>
              <p className="text-sm capitalize">{profile.age_group || uiText("Not specified")}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">{uiText("Phone")}</span>
              <p className="text-sm">{profile.phone || uiText("Not provided")}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">{uiText("Address")}</span>
              <p className="text-sm">{profile.address || uiText("Not provided")}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">{uiText("City")}</span>
              <p className="text-sm">{profile.city || uiText("Not provided")}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">{uiText("Postal Code")}</span>
              <p className="text-sm">{profile.postal_code || uiText("Not provided")}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">{uiText("Join Date")}</span>
              <p className="text-sm">{formatDate(profile.join_date)}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">{uiText("Belt Level")}</span>
              {beltLevel ? (
                <Badge className={`${getBeltColorClass(beltLevel.color)} text-xs`}>
                  {beltLevel.name}
                </Badge>
              ) : (
                <p className="text-sm">{uiText("No belt assigned")}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Emergency Contact */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5" />
            {uiText("Emergency Contact")}</CardTitle>
        </CardHeader>
        <CardContent>
          {emergencyContact ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <span className="text-sm font-medium text-gray-500">{uiText("Name")}</span>
                <p className="text-sm">{emergencyContact.name}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">{uiText("Phone")}</span>
                <p className="text-sm">{emergencyContact.phone}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">{uiText("Relationship")}</span>
                <p className="text-sm">{emergencyContact.relationship}</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">{uiText("No emergency contact information provided.")}</p>
          )}
        </CardContent>
      </Card>

      {/* Medical Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5" />
            {uiText("Medical Information")}</CardTitle>
        </CardHeader>
        <CardContent>
          {medicalInfo ? (
            <div className="space-y-4">
              <div>
                <span className="text-sm font-medium text-gray-500">{uiText("Medical Conditions")}</span>
                <p className="text-sm">{medicalInfo.medical_conditions || uiText("None reported")}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">{uiText("Current Medications")}</span>
                <p className="text-sm">{medicalInfo.current_medications || uiText("None reported")}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">{uiText("Allergies")}</span>
                <p className="text-sm">{medicalInfo.allergies || uiText("None reported")}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">{uiText("Injuries")}</span>
                <p className="text-sm">{medicalInfo.injuries || uiText("None reported")}</p>
              </div>
              {medicalInfo.doctor_name && (
                <>
                  <div>
                    <span className="text-sm font-medium text-gray-500">{uiText("Doctor Name")}</span>
                    <p className="text-sm">{medicalInfo.doctor_name}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">{uiText("Doctor Phone")}</span>
                    <p className="text-sm">{medicalInfo.doctor_phone}</p>
                  </div>
                </>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500">{uiText("No medical information provided.")}</p>
          )}
        </CardContent>
      </Card>

      {/* Martial Arts Background */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            {uiText("Martial Arts Background")}</CardTitle>
        </CardHeader>
        <CardContent>
          {martialArtsBackground ? (
            <div className="space-y-4">
              <div>
                <span className="text-sm font-medium text-gray-500">{uiText("Previous Experience")}</span>
                <p className="text-sm capitalize">{martialArtsBackground.previous_experience}</p>
              </div>
              {martialArtsBackground.previous_styles && (
                <div>
                  <span className="text-sm font-medium text-gray-500">{uiText("Previous Styles")}</span>
                  <p className="text-sm">{martialArtsBackground.previous_styles}</p>
                </div>
              )}
              {martialArtsBackground.years_of_experience && (
                <div>
                  <span className="text-sm font-medium text-gray-500">{uiText("Years of Experience")}</span>
                  <p className="text-sm">{martialArtsBackground.years_of_experience}</p>
                </div>
              )}
              {martialArtsBackground.previous_belt && (
                <div>
                  <span className="text-sm font-medium text-gray-500">{uiText("Previous Belt")}</span>
                  <p className="text-sm">{martialArtsBackground.previous_belt}</p>
                </div>
              )}
              <div>
                <span className="text-sm font-medium text-gray-500">{uiText("Training Goals")}</span>
                <p className="text-sm">{martialArtsBackground.training_goals || uiText("Not specified")}</p>
              </div>
              {martialArtsBackground.how_did_you_hear && (
                <div>
                  <span className="text-sm font-medium text-gray-500">{uiText("How did you hear about us?")}</span>
                  <p className="text-sm">{martialArtsBackground.how_did_you_hear}</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500">{uiText("No martial arts background information provided.")}</p>
          )}
        </CardContent>
      </Card>

      {/* Disclaimer Signature */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            {uiText("Liability Waiver & Agreement")}</CardTitle>
        </CardHeader>
        <CardContent>
          {disclaimerSignature ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <FileCheck className="h-4 w-4 text-green-600" />
                <Badge variant="secondary" className="bg-green-100 text-green-800">
                  {uiText("Waiver Signed")}</Badge>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="text-sm font-medium text-gray-500">{uiText("Digital Signature")}</span>
                  <p className="text-sm font-medium">{disclaimerSignature.digital_signature}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">{uiText("Signature Date")}</span>
                  <p className="text-sm">{formatDate(disclaimerSignature.signature_date)}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">{uiText("Agreement Status")}</span>
                  <p className="text-sm">
                    {disclaimerSignature.disclaimer_agreed ? (
                      <span className="text-green-600">{uiText("✓ Agreed to terms")}</span>
                    ) : (
                      <span className="text-red-600">{uiText("✗ Not agreed")}</span>
                    )}
                  </p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">{uiText("Signed At")}</span>
                  <p className="text-sm">{formatDate(disclaimerSignature.created_at)}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Badge variant="destructive">{uiText("No Waiver Signed")}</Badge>
              <span className="text-sm text-red-600">{uiText("This student has not signed the liability waiver.")}</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StudentDetailsView;